import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BarChart3, TrendingUp } from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import { getTransactions } from "../utils/storage";
import { Transaction } from "../types";
import Charts from "../components/Charts";
import Layout from "../components/Layout";
import BottomNavigation from "../components/BottomNavigation";

const ChartsPage: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const userTransactions = getTransactions(user.id);
    setTransactions(userTransactions);
  }, [user, navigate]);

  // Processar dados para os gráficos
  const processChartData = () => {
    const now = new Date();
    const last6Months = [];

    // Gerar dados dos últimos 6 meses
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString("pt-BR", { month: "short" });

      const monthTransactions = transactions.filter((t) => {
        const transactionDate = new Date(t.date);
        return (
          transactionDate.getMonth() === date.getMonth() &&
          transactionDate.getFullYear() === date.getFullYear()
        );
      });

      const income = monthTransactions
        .filter((t) => t.type === "income")
        .reduce((sum, t) => sum + t.amount, 0);

      const expenses = monthTransactions
        .filter((t) => t.type === "expense")
        .reduce((sum, t) => sum + t.amount, 0);

      last6Months.push({
        month: monthName,
        income,
        expenses,
        balance: income - expenses,
      });
    }

    // Dados por categoria
    const categoryMap = new Map<string, number>();
    transactions
      .filter((t) => t.type === "expense")
      .forEach((t) => {
        const current = categoryMap.get(t.category) || 0;
        categoryMap.set(t.category, current + t.amount);
      });

    const categoryData = Array.from(categoryMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 categorias

    return {
      categoryData,
      monthlyData: last6Months,
      balanceHistory: last6Months,
    };
  };

  const chartData = processChartData();

  if (!user) {
    return null;
  }

  return (
    <Layout
      title="Charts and Analysis"
      subtitle="Visualize your financial data in detail"
    >
      {/* Conteúdo principal */}
      {/* Título */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8"
      >
        <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <BarChart3 className="w-8 h-8" />
          Financial Analysis
        </h2>
        <p className="text-gray-400">
          Visualize your financial data clearly and objectively
        </p>
      </motion.div>

      {/* Gráficos */}
      <Charts
        categoryData={chartData.categoryData}
        monthlyData={chartData.monthlyData}
        balanceHistory={chartData.balanceHistory}
      />

      {/* Insights */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-8"
      >
        <h3 className="text-lg font-semibold text-white mb-4">
          Insights e Recomendações
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card bg-blue-500/10 border-blue-500/30">
            <div className="flex items-center space-x-3 mb-3">
              <span className="text-2xl">💡</span>
              <h4 className="text-lg font-semibold text-white">Savings Tip</h4>
            </div>
            <p className="text-gray-300 text-sm">
              {chartData.categoryData.length > 0
                ? `Your biggest spending category is "${chartData.categoryData[0].name}". Consider reviewing these expenses to save.`
                : "Start recording your transactions to receive personalized insights."}
            </p>
          </div>

          <div className="card bg-green-500/10 border-green-500/30">
            <div className="flex items-center space-x-3 mb-3">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
              <h4 className="text-lg font-semibold text-white">
                Tendência Positiva
              </h4>
            </div>
            <p className="text-gray-300 text-sm">
              {chartData.monthlyData.length > 0 &&
              chartData.monthlyData[chartData.monthlyData.length - 1].balance >
                0
                ? "Congratulations! You have a positive balance this month."
                : "Focus on reducing expenses or increasing income to improve your balance."}
            </p>
          </div>
        </div>
      </motion.div>

      <BottomNavigation />
    </Layout>
  );
};

export default ChartsPage;
